import type { Chord, Substitution } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { useState, type FC } from 'react'
import { ReharmAbToggle, type AbMode } from './reharm-ab-toggle.js'
import { useReharmLabPlayback } from './reharm-lab-playback.js'
import { ReharmProgressionStrip, type ActiveSubstitution } from './reharm-progression-strip.js'
import { ReharmSidePanel } from './reharm-side-panel.js'
import styles from './reharm-lab.module.css'

const DEFAULT_KEY: Scale = { root: 'C', type: 'major' }
const DEFAULT_PROGRESSION: Chord[] = [
  { root: 'C', variant: '' },
  { root: 'A', variant: 'm' },
  { root: 'F', variant: '' },
  { root: 'G', variant: '' },
]

const applySubstitution = (progression: Chord[], active: ActiveSubstitution | null): Chord[] => {
  if (!active) {return progression}
  const { slotIndex, substitution } = active
  switch (substitution.kind) {
    case 'replace':
      return progression.map((c, i) => (i === slotIndex ? substitution.chord : c))
    case 'insert-before':
      return [...progression.slice(0, slotIndex), substitution.chord, ...progression.slice(slotIndex)]
    case 'replace-next':
      return progression.map((c, i) => (i === slotIndex + 1 ? substitution.chord : c))
  }
}

/**
 * Reharm Lab v1 — the first creative tool in the Music-Theory Learning
 * suite. A 4-slot progression strip with a side panel offering the five
 * Bowie-locked substitution techniques; A/B toggle compares Original vs
 * Reharm. Lives at `/app/reharm`.
 *
 * Spec: docs/superpowers/specs/2026-05-19-reharm-lab-design.md
 */
export const ReharmLab: FC = () => {
  const [key] = useState<Scale>(DEFAULT_KEY)
  const [progression] = useState<Chord[]>(DEFAULT_PROGRESSION)
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null)
  const [activeSubstitution, setActiveSubstitution] = useState<ActiveSubstitution | null>(null)
  const [abMode, setAbMode] = useState<AbMode>('original')

  const effectiveProgression = abMode === 'reharm' ? applySubstitution(progression, activeSubstitution) : progression
  const { play } = useReharmLabPlayback({ chords: effectiveProgression })

  const handleApply = (slotIndex: number, substitution: Substitution): void => {
    if (!substitution.available) {return}
    setActiveSubstitution({ slotIndex, substitution })
    setAbMode('reharm')
  }

  const handleReset = (): void => {
    setActiveSubstitution(null)
    setAbMode('original')
  }

  return (
    <div className={styles.lab}>
      <header className={styles.header}>
        <h1 className={styles.title}>Reharm Lab</h1>
      </header>

      <section className={styles.workspace}>
        <div className={styles.left}>
          <ReharmProgressionStrip
            progression={progression}
            keyScale={key}
            activeSubstitution={activeSubstitution}
            selectedSlotIndex={selectedSlotIndex}
            onSlotClick={setSelectedSlotIndex}
          />
          <div className={styles.controls}>
            <ReharmAbToggle
              hasReharm={activeSubstitution !== null}
              abMode={abMode}
              onModeChange={setAbMode}
              onPlay={play}
            />
            {activeSubstitution !== null ? (
              <button type="button" className={styles.resetButton} onClick={handleReset}>
                Reset to Original
              </button>
            ) : null}
          </div>
        </div>

        <ReharmSidePanel
          selectedSlotIndex={selectedSlotIndex}
          progression={progression}
          keyScale={key}
          activeSubstitution={activeSubstitution}
          onApply={handleApply}
        />
      </section>
    </div>
  )
}
